import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";
import type { AnswerType } from "../components/questionnaires/QuestionInput";
import { useClientStore } from "./useClientStore";
import { useProjectStore } from "./useProjectStore";

export type LayoutType = "form" | "card";

interface QuestionnaireInput {
  name: string;
  description: string;
  projectId?: string;
  clientId: string;
  serviceType: string;
  layoutType?: LayoutType;
  questions: { question: string; answer?: any }[];
}

interface Questionnaire {
  _id: string;
  name: string;
  description: string;
  projectId: any;
  clientId: any;
  serviceType: string;
  layoutType?: LayoutType;

  questions: {
    _id: string;
    required: boolean;
    question: string;
    answerType: AnswerType;
    options?: string[];
    answer?: any;

    // ── Sectioned, branching questionnaires ──────────────────────────────────
    // The richer intake templates group their questions into named steps,
    // explain each step, offer described choices, and skip whole sections that
    // do not apply. All optional — an existing questionnaire has none of them
    // and renders exactly as before.
    section?: string;
    sectionTitle?: string;
    sectionIntro?: string;
    helpText?: string;
    optionDetails?: { value: string; label: string; description?: string }[];
    pairs?: { left: string; right: string }[];
    /** Stable handle a showWhen can point at. */
    key?: string;
    /** Shown only when an earlier answer matches; absent means always shown. */
    showWhen?: { questionKey?: string; anyOf?: string[] };
    order?: number;
  }[];

  isSubmitted: boolean;
  submittedAt?: string;
}
interface QuestionnaireState {
  questionnaires: Questionnaire[];
  publicQuestionnaire: Questionnaire | null;
  isCreating: boolean;
  isFetching: boolean;
  isSubmitting: boolean;
  isDeleting: boolean;
  isUpdating: boolean;

  createQuestionnaire: (data: QuestionnaireInput) => Promise<void>;
  getQuestionnaire: (id: string) => Promise<Questionnaire | null>;
  getAllQuestionnaires: () => Promise<void>;
  submitQuestionnaire: (id: string, questions: any[]) => Promise<void>;
  deleteQuestionnaire: (id: string) => Promise<void>;
  updateQuestionnaire: (id: string, data: Partial<Questionnaire>) => Promise<void>;
  fetchPublicQuestionnaire: (token: string) => Promise<void>;
  submitPublicQuestionnaire: (token: string, answers: Record<string, any>) => Promise<boolean>;
}

export const useQuestionnaireStore = create<QuestionnaireState>((set, get) => ({
  questionnaires: [],
  publicQuestionnaire: null,
  isCreating: false,
  isFetching: false,
  isSubmitting: false,
  isDeleting: false,
  isUpdating:false,

  createQuestionnaire: async (data) => {
    set({ isCreating: true });
    try {

      const res = await axiosInstance.post("/questionnaire", data);
     const clients = useClientStore.getState().clients;
     const projects = useProjectStore.getState().projects

     const fullAssignedContact =

     res.data.questionnaire.clientId
        ? clients.find(c => c._id === res.data.questionnaire.clientId)
        : res.data.questionnaire.clientId;

    const AssignedProject =
    res.data.questionnaire.projectId ?
        projects.find(p=>p._id === res.data.questionnaire.projectId)
        : res.data.questionnaire.cleints

      const questionnaireWithFullData = {
      ...res.data.questionnaire,
      clientId: fullAssignedContact,
      projectId:AssignedProject,
    };
      set({
        questionnaires: [questionnaireWithFullData, ...get().questionnaires],
      });
      toast.success("Questionnaire created");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to create questionnaire");
    } finally {
      set({ isCreating: false });
    }
  },

  getQuestionnaire: async (id) => {
    set({ isFetching: true });
    try {
      const res = await axiosInstance.get(`/questionnaire/${id}`);
      return res.data;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load questionnaire");
      return null;
    } finally {
      set({ isFetching: false });
    }
  },

  getAllQuestionnaires: async () => {
    set({ isFetching: true });
    try {
      const res = await axiosInstance.get("/questionnaire");
      set({ questionnaires: res.data });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load questionnaires");
    } finally {
      set({ isFetching: false });
    }
  },

  submitQuestionnaire: async (id, questions) => {
    set({ isSubmitting: true });
    try {
      await axiosInstance.put(`/questionnaire/${id}/submit`, { questions });
      toast.success("Submitted successfully");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to submit");
    } finally {
      set({ isSubmitting: false });
    }
  },

updateQuestionnaire: async (id: string, data: Partial<QuestionnaireInput>) => {
  set({ isUpdating: true });
  try {
    const res = await axiosInstance.put(`/questionnaire/${id}`, data);
    set((state) => ({
      questionnaires: state.questionnaires.map((q) =>
        q._id === id ? res.data.questionnaire : q
      ),
    }));
    toast.success("Questionnaire updated");
  } catch (err: any) {
    toast.error(err.response?.data?.message || "Failed to update questionnaire");
  } finally {
    set({ isUpdating: false });
  }
},

  deleteQuestionnaire: async (id: string) => {
    set({ isDeleting: true });
    try {
      await axiosInstance.delete(`/questionnaire/${id}`);
      set({
        questionnaires: get().questionnaires.filter(q => q._id !== id),
      });
      toast.success("Questionnaire deleted successfully");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to delete questionnaire");
    } finally {
      set({ isDeleting: false });
    }
  },

  fetchPublicQuestionnaire: async (token) => {
    set({ isFetching: true });
    try {
      const res = await axiosInstance.get(`/questionnaire/public/${token}`);
      set({ publicQuestionnaire: res.data });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load questionnaire");
    } finally {
      set({ isFetching: false });
    }
  },

  submitPublicQuestionnaire: async (token, data) => {
    set({ isSubmitting: true });
    try {
      await axiosInstance.put(`/questionnaire/public/${token}/submit`, data);
      toast.success("Submitted successfully!");
      return true;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to submit answers");
      return false;
    } finally {
      set({ isSubmitting: false });
    }
  },

}));

